import React, { memo } from 'react';
import { useFrame } from '~/shared/utils/frame';
import Script from 'next/script';
import { useChat } from '../useChat';
import { useDixa } from './useDixa';
import { useEffectOnce } from 'react-use';
import { useTheme } from '@emotion/react';

const dixaScriptUrl = 'https://widget.dixa.io/assets/scripts/javascript/loader.js';

export const Dixa = memo(() => {
    const { data: frame } = useFrame();
    const { initDixa, showDixa, setLanguage, setTheme } = useDixa();
    const { setChatState, setChatError } = useChat();
    const theme = useTheme();

    const handleDixaScriptLoaded = () => {
        showDixa();
        setChatState('open'); // Hide custom button, just go with Dixa's button, because we can't easily hide theirs.
    };

    useEffectOnce(() => {
        // Make sure country/region from culture is uppercase
        const culture = frame?.culture || 'en-gb';
        const tempCultureArray = culture.split('-');
        tempCultureArray[1] &&= tempCultureArray[1].toUpperCase();
        let formattedCulture = tempCultureArray.join('-');
        let id: string;

        switch (formattedCulture) {
            case 'sv':
                id = '2adb65ac-087e-4eff-baf1-61b4e25a5928';
                break;
            case 'nl':
                id = '2228c829-92a0-4bd3-b190-5a9fe7ffe359';
                break;
            case 'fi':
            case 'fi-FI':
                id = '851f2082-c7ea-4804-aceb-c6583d117016';
                break;
            case 'de-DE':
            case 'de-AT':
            default:
                // Dixa only supports 'de-DE'.
                formattedCulture = formattedCulture.replace('AT', 'DE');
                id = '2943bfce-e77d-4eb5-ad39-5d78359fb54c';
                break;
        }
        initDixa(id);
        setLanguage(formattedCulture);
        setTheme(theme.colors.dark);
    });

    const handleDixaScriptError = (e: Error) => {
        setChatError(e);
    };

    return (
        <Script
            onLoad={handleDixaScriptLoaded}
            onError={handleDixaScriptError}
            async={true}
            charSet="utf-8"
            src={dixaScriptUrl}
        />
    );
});
